import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Check, ArrowRight, Layers, TrendingUp, PieChart, Target, 
  Megaphone, Rocket, DollarSign, Package, Zap, Brain, Shield,
  BarChart3, LineChart, Activity, Calculator, Users, Clock,
  RefreshCw, AlertTriangle, Percent, Tag, Star, ChevronDown, ChevronUp
} from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import CurrencySelector from '../components/CurrencySelector';

const FeaturesPage = () => {
  const [expandedSuite, setExpandedSuite] = useState(null);
  const { format, currentCurrency } = useCurrency();
  const perFeaturePrice = 49; // Base price in USD

  const featureSuites = [
    {
      id: 'sku-optimization',
      name: 'SKU Optimization Suite',
      icon: Package,
      color: 'from-blue-500 to-blue-600',
      description: 'Essential pricing optimization toolkit for individual product management',
      bundlePrice: 225,
      featureCount: 7,
      highlights: ['Core pricing engine', 'A/B testing', 'Margin protection'],
      features: [
        {
          name: 'Price Optimization',
          icon: Target,
          description: 'AI-driven optimal price point discovery using elasticity modeling and demand curves',
          benefits: ['Find the perfect price point', 'Maximize revenue per SKU', 'Data-driven recommendations'],
          included: true
        },
        {
          name: 'Revenue Maximization',
          icon: TrendingUp,
          description: 'Algorithms focused on driving top-line revenue growth across your product catalog',
          benefits: ['Volume-based optimization', 'Revenue forecasting', 'Growth trajectory planning']
        },
        {
          name: 'A/B Testing',
          icon: RefreshCw,
          description: 'Run controlled pricing experiments to validate strategies before full rollout',
          benefits: ['Statistical significance testing', 'Control vs test groups', 'Real-time results tracking']
        },
        {
          name: 'Price Increase Impact',
          icon: BarChart3,
          description: 'Simulate and predict the impact of price increases on volume and revenue',
          benefits: ['Volume elasticity modeling', 'Revenue impact forecasts', 'Risk assessment']
        },
        {
          name: 'Discount Optimization',
          icon: Percent,
          description: 'Find the optimal discount levels that drive conversions without eroding margins',
          benefits: ['Discount depth optimization', 'Promo ROI tracking', 'Margin-aware discounting']
        },
        {
          name: 'Positioning Strategy',
          icon: Tag,
          description: 'Position products optimally within your portfolio and against competitors',
          benefits: ['Good-better-best pricing', 'Competitive positioning', 'Portfolio balance']
        },
        {
          name: 'Margin Protection',
          icon: Shield,
          description: 'Automated guardrails to prevent pricing decisions that erode profitability',
          benefits: ['Floor price enforcement', 'Margin alerts', 'Approval workflows']
        }
      ]
    },
    {
      id: 'category-management',
      name: 'Category Management Suite',
      icon: Layers,
      color: 'from-purple-500 to-purple-600',
      description: 'Multi-category optimization for retailers managing diverse product portfolios',
      bundlePrice: 199,
      featureCount: 6,
      highlights: ['Cross-category pricing', 'Profit maximization', 'Rules engine'],
      features: [
        {
          name: 'Category Optimization',
          icon: Layers,
          description: 'Optimize pricing across entire product categories while maintaining internal coherence',
          benefits: ['Category-level analytics', 'Price ladder management', 'Coherent pricing structures'],
          included: true
        },
        {
          name: 'Top N Focus',
          icon: Star,
          description: 'Prioritize optimization efforts on your highest-impact products',
          benefits: ['Pareto analysis', '80/20 optimization', 'Focus on winners']
        },
        {
          name: 'Price Harmonization',
          icon: Activity,
          description: 'Ensure consistent pricing logic across channels, regions, and customer segments',
          benefits: ['Channel consistency', 'Regional alignment', 'Segment coherence']
        },
        {
          name: 'Profit Maximization',
          icon: DollarSign,
          description: 'Algorithms optimized for bottom-line profitability, not just revenue',
          benefits: ['Margin-focused optimization', 'Cost-aware pricing', 'Profit per unit tracking']
        },
        {
          name: 'Weighted Metrics',
          icon: Calculator,
          description: 'Customize optimization goals with weighted importance across multiple metrics',
          benefits: ['Custom KPI weights', 'Multi-objective optimization', 'Balanced scorecards']
        },
        {
          name: 'Pricing Rules Engine',
          icon: Shield,
          description: 'Define business rules that automatically govern pricing decisions',
          benefits: ['If-then logic rules', 'Constraint management', 'Policy enforcement']
        }
      ]
    },
    {
      id: 'demand-forecasting',
      name: 'Demand Forecasting Suite',
      icon: LineChart,
      color: 'from-green-500 to-green-600',
      description: 'Enterprise-grade ML forecasting to predict demand and optimize inventory',
      bundlePrice: 149,
      featureCount: 4,
      highlights: ['ML forecasting', 'Real-time sensing', 'New product predictions'],
      features: [
        {
          name: 'Time-Series Forecasting',
          icon: LineChart,
          description: 'Advanced ML models for accurate demand prediction across time horizons',
          benefits: ['Short/long term forecasts', 'Seasonality detection', 'Trend analysis'],
          included: true
        },
        {
          name: 'Causal Modeling',
          icon: Brain,
          description: 'Understand the drivers behind demand changes with causal inference',
          benefits: ['Driver identification', 'What-if scenarios', 'Root cause analysis']
        },
        {
          name: 'New Product Forecasting',
          icon: Rocket,
          description: 'Predict demand for products without historical data using analog matching',
          benefits: ['Analog product matching', 'Launch predictions', 'Ramp-up modeling']
        },
        {
          name: 'Real-Time Demand Sensing',
          icon: Activity,
          description: 'Capture demand signals in real-time to adjust forecasts dynamically',
          benefits: ['Live signal processing', 'Rapid adjustments', 'Market responsiveness']
        }
      ]
    },
    {
      id: 'portfolio-optimization',
      name: 'Portfolio Optimization Suite',
      icon: PieChart,
      color: 'from-orange-500 to-orange-600',
      description: 'Cross-product optimization for maximum portfolio performance',
      bundlePrice: 125,
      featureCount: 4,
      highlights: ['Portfolio rebalancing', 'Elasticity mapping', 'Risk assessment'],
      features: [
        {
          name: 'Portfolio Rebalancing',
          icon: RefreshCw,
          description: 'Optimize the mix and pricing of products to maximize overall portfolio value',
          benefits: ['Mix optimization', 'Cannibalization management', 'Portfolio health scores']
        },
        {
          name: 'Elasticity Mapping',
          icon: TrendingUp,
          description: 'Visual mapping of price elasticity across your entire product portfolio',
          benefits: ['Elasticity heatmaps', 'Cross-elasticity analysis', 'Sensitivity visualization']
        },
        {
          name: 'Multi-Category Promotions',
          icon: Megaphone,
          description: 'Coordinate promotional pricing across categories for maximum impact',
          benefits: ['Bundle promotions', 'Cross-sell optimization', 'Promo calendar sync']
        },
        {
          name: 'Risk Assessment',
          icon: AlertTriangle,
          description: 'Identify and quantify pricing risks across your portfolio',
          benefits: ['Risk scoring', 'Vulnerability analysis', 'Mitigation recommendations']
        }
      ]
    },
    {
      id: 'strategic-planning',
      name: 'Strategic Planning Suite',
      icon: Target,
      color: 'from-indigo-500 to-indigo-600',
      description: 'Long-term pricing strategy development and competitive positioning',
      bundlePrice: 99,
      featureCount: 3,
      highlights: ['Strategic pricing', 'Competitive response', 'Market positioning'],
      features: [
        {
          name: 'Strategic Pricing',
          icon: Target,
          description: 'Develop long-term pricing strategies aligned with business objectives',
          benefits: ['Strategy frameworks', 'Goal alignment', 'Roadmap development']
        },
        {
          name: 'Competitive Response',
          icon: Shield,
          description: 'Monitor competitors and develop automated response strategies',
          benefits: ['Competitor tracking', 'Response playbooks', 'Market intelligence']
        },
        {
          name: 'Market Positioning',
          icon: Star,
          description: 'Position your products strategically within the market landscape',
          benefits: ['Perception mapping', 'Value positioning', 'Premium/value strategies']
        }
      ]
    },
    {
      id: 'promotional-intelligence',
      name: 'Promotional Intelligence Suite',
      icon: Megaphone,
      color: 'from-pink-500 to-pink-600',
      description: 'Smart promotion management for maximum ROI on marketing spend',
      bundlePrice: 75,
      featureCount: 2,
      highlights: ['Promo optimization', 'Impact analysis'],
      features: [
        {
          name: 'Promotion Optimization',
          icon: Megaphone,
          description: 'Optimize promotional pricing for maximum lift and ROI',
          benefits: ['Promo depth optimization', 'Timing optimization', 'Channel selection']
        },
        {
          name: 'Promotional Impact Analysis',
          icon: BarChart3,
          description: 'Measure and analyze the true impact of promotional activities',
          benefits: ['Incrementality measurement', 'Halo effect tracking', 'Post-promo analysis']
        }
      ]
    },
    {
      id: 'new-product-launch',
      name: 'New Product Launch Suite',
      icon: Rocket,
      color: 'from-cyan-500 to-cyan-600',
      description: 'Launch pricing strategy for successful new product introductions',
      bundlePrice: 75,
      featureCount: 2,
      highlights: ['Launch pricing', 'Forecast modeling'],
      features: [
        {
          name: 'Launch Pricing Strategy',
          icon: Rocket,
          description: 'Develop optimal pricing strategies for new product launches',
          benefits: ['Penetration vs skimming', 'Launch price modeling', 'Early adopter pricing']
        },
        {
          name: 'New Product Forecasting',
          icon: LineChart,
          description: 'Predict demand for new products using analog matching and market analysis',
          benefits: ['Analog-based forecasts', 'Market size estimation', 'Growth curve modeling']
        }
      ]
    },
    {
      id: 'financial-planning',
      name: 'Financial Planning Suite',
      icon: DollarSign,
      color: 'from-emerald-500 to-emerald-600',
      description: 'Revenue and profit modeling for finance-aligned pricing decisions',
      bundlePrice: 99,
      featureCount: 3,
      highlights: ['Revenue modeling', 'P&L integration', 'Profit planning'],
      features: [
        {
          name: 'Revenue Modeling',
          icon: TrendingUp,
          description: 'Build comprehensive revenue models tied to pricing scenarios',
          benefits: ['Scenario modeling', 'Revenue waterfalls', 'Growth projections']
        },
        {
          name: 'Profit Planning',
          icon: Calculator,
          description: 'Plan and forecast profitability across products and time periods',
          benefits: ['Profit forecasts', 'Margin planning', 'Cost integration']
        },
        {
          name: 'P&L Statement View',
          icon: DollarSign,
          description: 'See the direct P&L impact of pricing decisions in real-time',
          benefits: ['Live P&L impact', 'Financial dashboards', 'CFO-ready reports']
        }
      ]
    }
  ];

  const totalFeatures = featureSuites.reduce((acc, suite) => acc + suite.featureCount, 0);

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
            <Zap className="w-4 h-4 text-[#2563EB]" />
            <span className="text-[#2563EB] font-medium text-sm">{totalFeatures} Pricing Tools</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight">
            Everything You Need to
            <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> Price with Confidence</span>
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto mb-6">
            8 feature suites, {totalFeatures} tools. See what's safe, spot what's risky, and make pricing decisions you can defend.
          </p>
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="text-[#94A3B8] text-sm">Currency:</span>
            <CurrencySelector />
          </div>
          <div className="flex justify-center gap-4">
            <Link to="/pricing">
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-lg">
                View Pricing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/coming-soon">
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-lg">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-[#111827] border-y border-[#2563EB]/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '8', label: 'Feature Suites' },
              { value: `${totalFeatures}`, label: 'Individual Features' },
              { value: format(perFeaturePrice), label: 'Per Feature' },
              { value: '34%', label: 'Bundle Savings' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-bold text-[#2563EB] mb-1">{stat.value}</div>
                <div className="text-[#94A3B8]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Suites */}
      <section className="py-20 px-6 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#F8FAFC] mb-4">
              Feature <span className="text-[#2563EB]">Suites</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Our features are organized into 8 specialized suites. Buy individual features or save with bundles.
            </p>
          </div>

          <div className="space-y-6">
            {featureSuites.map((suite) => {
              const Icon = suite.icon;
              const isExpanded = expandedSuite === suite.id;
              
              return (
                <div
                  key={suite.id}
                  className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl overflow-hidden transition-all hover:border-[#2563EB]/40"
                >
                  {/* Suite Header */}
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => setExpandedSuite(isExpanded ? null : suite.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 bg-gradient-to-br ${suite.color} rounded-xl flex items-center justify-center`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-[#F8FAFC]">{suite.name}</h3>
                          <p className="text-[#94A3B8] text-sm">{suite.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden md:block">
                          <div className="text-2xl font-bold text-[#F8FAFC]">{format(suite.bundlePrice)}<span className="text-sm text-[#94A3B8]">/mo</span></div>
                          <div className="text-sm text-green-400">{suite.featureCount} features included</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="hidden md:flex gap-2">
                            {suite.highlights.map((highlight, idx) => (
                              <span key={idx} className="bg-[#2563EB]/10 text-[#2563EB] text-xs px-3 py-1 rounded-full">
                                {highlight}
                              </span>
                            ))}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-6 h-6 text-[#94A3B8]" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-[#94A3B8]" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Features */}
                  {isExpanded && (
                    <div className="border-t border-[#2563EB]/20 p-6 bg-[#0A0F1C]/50">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {suite.features.map((feature, idx) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <div
                              key={idx}
                              className={`p-4 rounded-xl border transition-all ${
                                feature.included 
                                  ? 'bg-[#2563EB]/10 border-[#2563EB]/30' 
                                  : 'bg-[#111827]/50 border-[#2563EB]/10 hover:border-[#2563EB]/30'
                              }`}
                            >
                              <div className="flex items-start gap-3 mb-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  feature.included ? 'bg-[#2563EB]' : 'bg-[#2563EB]/20'
                                }`}>
                                  <FeatureIcon className={`w-5 h-5 ${feature.included ? 'text-white' : 'text-[#2563EB]'}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-[#F8FAFC] font-semibold">{feature.name}</h4>
                                    {feature.included && (
                                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Included</span>
                                    )}
                                  </div>
                                  <p className="text-[#94A3B8] text-sm mt-1">{feature.description}</p>
                                </div>
                              </div>
                              <div className="space-y-1 ml-13">
                                {feature.benefits.map((benefit, bidx) => (
                                  <div key={bidx} className="flex items-center gap-2 text-xs">
                                    <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                                    <span className="text-[#94A3B8]">{benefit}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-6 pt-4 border-t border-[#2563EB]/10 flex items-center justify-between">
                        <div className="text-sm text-[#94A3B8]">
                          Individual features: <span className="text-[#F8FAFC]">{format(suite.featureCount * perFeaturePrice)}/mo</span>
                          <span className="mx-2">→</span>
                          Bundle price: <span className="text-green-400 font-semibold">{format(suite.bundlePrice)}/mo</span>
                          <span className="ml-2 text-green-400">(Save {format((suite.featureCount * perFeaturePrice) - suite.bundlePrice)})</span>
                        </div>
                        <Link to="/pricing">
                          <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
                            Add to Plan
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* All Features Grid */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#F8FAFC] mb-4">
              All {totalFeatures} <span className="text-[#2563EB]">Features</span> at a Glance
            </h2>
            <p className="text-[#94A3B8] text-lg">
              Quick reference of every feature available on PriceStrike
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {featureSuites.map((suite) => (
              <div key={suite.id} className="bg-[#111827]/50 border border-[#2563EB]/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <suite.icon className="w-5 h-5 text-[#2563EB]" />
                  <h4 className="text-[#F8FAFC] font-semibold text-sm">{suite.name.replace(' Suite', '')}</h4>
                </div>
                <ul className="space-y-2">
                  {suite.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs">
                      <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                      <span className="text-[#94A3B8]">{feature.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#0A0F1C]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#F8FAFC] mb-6">
            Ready to Price with <span className="text-[#2563EB]">Confidence</span>?
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8">
            See where your pricing is safe — or try our free pricing health check first.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/coming-soon">
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing-check">
              <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-10 py-7 text-lg font-semibold rounded-lg">
                Run Free Pricing Check
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
